import React from 'react';
import { Check, ArrowRight, Info, Shield, Zap, Building2, Crown, Mail, UserCheck } from 'lucide-react';

interface PricingProps {
    onBack: () => void;
}

const PLANS = [
    {
        key: 'free',
        name: 'Free',
        description: 'Dla każdego (domyślny)',
        price: '0',
        currency: 'PLN',
        interval: '/mies',
        features: [
            'Weryfikacja statusu podmiotu',
            'Weryfikacja reprezentantów',
            'Weryfikacja sposobu reprezentacji',
            'Dodatkowe informacje o firmie',
            'Limit raportów: 10'
        ],
        highlight: false,
        icon: UserCheck,
        color: 'bg-slate-400',
        isDefault: true
    },
    {
        key: 'start',
        name: 'Start',
        description: 'Idealny na początek',
        price: '25',
        currency: 'PLN',
        interval: '/mies',
        features: [
            'Wszystko w planie Free',
            'Szczegółowa reprezentacja firmy',
            'Historia zapytań',
            'Historia płatności',
            'Raport PDF',
            'Limit raportów: 5',
            'Status podmiotu: Bez limitu'
        ],
        highlight: false,
        icon: Zap,
        color: 'bg-emerald-500'
    },
    {
        key: 'pro',
        name: 'Pro',
        description: 'Dla profesjonalistów',
        price: '125',
        currency: 'PLN',
        interval: '/mies',
        features: [
            'Wszystko w planie Start',
            'Odpis z KRS w PDF',
            'Limit raportów: 25',
            'Priorytetowe wsparcie'
        ],
        highlight: true,
        icon: Crown,
        color: 'bg-sky-500'
    },
    {
        key: 'business',
        name: 'Business',
        description: 'Dla rosnących firm',
        price: '500',
        currency: 'PLN',
        interval: '/mies',
        features: [
            'Wszystko w planie Pro',
            'Raport JSON',
            'Dostępne REST API',
            'Limit raportów: 100'
        ],
        highlight: false,
        icon: Building2,
        color: 'bg-slate-700'
    },
    {
        key: 'enterprise',
        name: 'Enterprise',
        description: 'Rozwiązania dedykowane',
        price: 'Zapytaj',
        currency: '',
        interval: '',
        features: [
            'Wszystko w planie Business',
            'Dedykowane kontenery',
            'Izolacja danych'
        ],
        highlight: false,
        icon: Shield,
        color: 'bg-teal-700'
    }
];

const Pricing: React.FC<PricingProps> = ({ onBack }) => {
    const registerUrl = `${(import.meta as any).env.VITE_APP_URL || '__VITE_APP_URL_PLACEHOLDER__'}/register`;

    return (
        <div className="bg-white min-h-screen">
            {/* Hero Header */}
            <section className="bg-primary py-20 px-4 md:px-6 relative overflow-hidden">
                <div className="absolute inset-0 z-0 opacity-20">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-accent/20 via-transparent to-transparent"></div>
                </div>

                <div className="w-4/5 mx-auto relative z-10 text-center">
                    <button
                        onClick={onBack}
                        className="inline-flex items-center text-slate-400 hover:text-white mb-8 transition-colors text-sm font-medium"
                    >
                        <ArrowRight className="w-4 h-4 mr-2 rotate-180" />
                        Powrót do strony głównej
                    </button>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-6 font-headline">
                        Prosty i Przejrzysty Cennik
                    </h1>
                    <p className="text-lg md:text-xl text-slate-400 max-w-2xl mx-auto">
                        Wybierz plan dopasowany do skali Twojej działalności.
                        Bez ukrytych kosztów i automatycznych subskrypcji.
                    </p>
                </div>
            </section>

            {/* Pricing Grid */}
            <section className="py-24 px-4 md:px-6 -mt-12 relative z-20">
                <div className="w-full max-w-[90rem] mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
                        {PLANS.map((plan) => (
                            <div
                                key={plan.key}
                                className={`flex flex-col rounded-2xl border transition-all duration-300 hover:shadow-2xl ${plan.highlight
                                    ? 'border-sky-500 shadow-xl scale-105 bg-white ring-4 ring-sky-500/10'
                                    : 'border-slate-200 bg-white hover:border-slate-300'
                                    }`}
                            >
                                {plan.highlight && (
                                    <div className="bg-sky-500 text-white text-center py-1.5 text-xs font-bold uppercase tracking-widest rounded-t-xl">
                                        Najczęściej wybierany
                                    </div>
                                )}

                                <div className="p-8 flex-grow">
                                    <div className={`w-12 h-12 rounded-xl ${plan.color} flex items-center justify-center text-white mb-6`}>
                                        <plan.icon className="w-6 h-6" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-primary mb-2">{plan.name}</h3>
                                    <p className="text-slate-500 mb-6 text-sm">{plan.description}</p>

                                    <div className="flex items-baseline mb-8">
                                        <span className="text-4xl font-extrabold text-primary">{plan.price}</span>
                                        {plan.currency && <span className="text-xl font-medium text-slate-500 ml-1">{plan.currency}</span>}
                                        {plan.interval && <span className="text-slate-400 ml-1">{plan.interval}</span>}
                                    </div>

                                    <ul className="space-y-4 mb-8">
                                        {plan.features.map((feature, idx) => (
                                            <li key={idx} className="flex items-start text-sm">
                                                <Check className="w-5 h-5 text-emerald-500 mr-3 shrink-0" />
                                                <span className="text-slate-600">{feature}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <div className="p-8 pt-0 mt-auto">
                                    <a
                                        href={plan.key === 'enterprise' ? '#faq-contact' : registerUrl}
                                        onClick={(e) => {
                                            if (plan.key === 'enterprise') {
                                                e.preventDefault();
                                                document.getElementById('faq-contact')?.scrollIntoView({ behavior: 'smooth' });
                                            }
                                        }}
                                        className={`w-full inline-flex items-center justify-center h-12 rounded-xl font-bold text-sm transition-all ${plan.highlight
                                            ? 'bg-sky-500 text-white hover:bg-sky-600 shadow-lg shadow-sky-500/20'
                                            : 'bg-primary text-white hover:bg-primary-hover shadow-lg shadow-primary/10'
                                            }`}
                                    >
                                        {plan.key === 'enterprise' ? 'Skontaktuj się' : 'Wybierz'}
                                    </a>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Model Explanation */}
            <section id="how-it-works" className="py-24 bg-slate-50">
                <div className="w-4/5 mx-auto">
                    <div className="max-w-4xl mx-auto bg-white rounded-3xl p-8 md:p-16 shadow-sm border border-slate-100">
                        <div className="flex items-center mb-12">
                            <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center mr-6">
                                <Info className="w-8 h-8 text-accent" />
                            </div>
                            <h2 className="text-3xl font-bold text-primary font-headline">Zasady korzystania z usług</h2>
                        </div>

                        <div className="grid md:grid-cols-2 gap-12">
                            <div className="space-y-4">
                                <h4 className="text-xl font-bold text-primary flex items-center">
                                    <span className="w-2 h-2 bg-accent rounded-full mr-3"></span>
                                    Model Prepaid
                                </h4>
                                <p className="text-slate-600 leading-relaxed">
                                    Verifiko działa w modelu przedpłaconym. Kupujesz pakiet weryfikacji wtedy, gdy go potrzebujesz. Nie stosujemy automatycznych płatności ani odnawiania subskrypcji.
                                </p>
                            </div>

                            <div className="space-y-4">
                                <h4 className="text-xl font-bold text-primary flex items-center">
                                    <span className="w-2 h-2 bg-accent rounded-full mr-3"></span>
                                    Ważność i Limity
                                </h4>
                                <p className="text-slate-600 leading-relaxed">
                                    Każdy plan jest aktywny przez 30 dni lub do wykorzystania limitu zapytań. Przy zakupie kolejnego planu przed czasem, niewykorzystane limity sumują się.
                                </p>
                            </div>
                        </div>

                        <div className="mt-16 p-6 bg-primary/5 rounded-2xl border border-primary/10 flex items-start">
                            <Mail className="w-6 h-6 text-primary mr-4 mt-1 shrink-0" />
                            <div>
                                <p className="text-primary font-medium mb-1">Potrzebujesz oferty dedykowanej?</p>
                                <p className="text-slate-600">Napisz do nas na <a href="mailto:kontakt@verifiko.pl" className="text-accent font-bold hover:underline">kontakt@verifiko.pl</a>.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ CTA */}
            <section id="faq-contact" className="py-20 bg-white text-center">
                <div className="w-4/5 mx-auto">
                    <h2 className="text-2xl font-bold text-primary mb-6">Masz dodatkowe pytania?</h2>
                    <div className="flex flex-col sm:flex-row justify-center gap-4">
                        <a
                            href="mailto:kontakt@verifiko.pl"
                            className="inline-flex items-center justify-center h-12 px-8 bg-slate-100 text-primary font-bold rounded-xl hover:bg-slate-200 transition-all"
                        >
                            Napisz do nas
                        </a>
                        <a
                            href="tel:+48518929623"
                            className="inline-flex items-center justify-center h-12 px-8 border border-slate-200 text-primary font-bold rounded-xl hover:bg-slate-50 transition-all"
                        >
                            Zadzwoń: +48 518 929 623
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Pricing;
